/**
 * 收发通/进口商：按国税号标签与格式校验
 */

const EU_COUNTRY_KEYS = [
  "德国", "法国", "意大利", "西班牙", "荷兰", "比利时", "波兰", "瑞典", "奥地利",
  "爱尔兰", "葡萄牙", "捷克", "匈牙利", "罗马尼亚", "希腊", "芬兰", "丹麦",
  "斯洛伐克", "保加利亚", "克罗地亚", "立陶宛", "斯洛文尼亚", "拉脱维亚",
  "爱沙尼亚", "卢森堡", "马耳他", "塞浦路斯", "欧盟", "europe", "european"
];

export function normalizeCountryKey(country) {
  return String(country || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");
}

export function detectRegion(country) {
  const key = normalizeCountryKey(country);
  if (!key) return "OTHER";
  if (key.includes("美国") || key === "us" || key === "usa" || key.includes("unitedstates")) return "US";
  if (key.includes("加拿大") || key === "ca" || key.includes("canada")) return "CA";
  if (key.includes("日本") || key === "jp" || key.includes("japan")) return "JP";
  if (key.includes("墨西哥") || key === "mx" || key.includes("mexico")) return "MX";
  if (key.includes("巴西") || key === "br" || key.includes("brazil") || key.includes("brasil")) return "BR";
  if (
    key.includes("英国") ||
    key === "uk" ||
    key.includes("unitedkingdom") ||
    key.includes("greatbritain") ||
    key === "gb"
  ) {
    return "UK";
  }
  if (EU_COUNTRY_KEYS.some(c => key.includes(normalizeCountryKey(c)))) return "EU";
  return "OTHER";
}

export function resolveImporterTaxMeta(country) {
  const region = detectRegion(country);
  const base = {
    region,
    taxIdType: "OTHER",
    vatLabel: "税号",
    vatPlaceholder: "按国家填写对应税号",
    showEori: false,
    eoriLabel: "EORI",
    eoriPlaceholder: "选填",
    showIoss: false,
    showBond: false,
    showIe: false,
    stateLabel: "州/省",
    statePlaceholder: "州或省",
    zipLabel: "邮编",
    zipPlaceholder: "邮编",
    cityLabel: "城市",
    cityPlaceholder: "城市"
  };

  if (region === "US") {
    return {
      ...base,
      taxIdType: "EIN",
      vatLabel: "EIN",
      vatPlaceholder: "美国雇主识别号，如 12-3456789",
      showBond: true,
      stateLabel: "州",
      statePlaceholder: "如 CA / NY",
      zipLabel: "ZIP",
      zipPlaceholder: "如 90001",
      cityLabel: "城市",
      cityPlaceholder: "City"
    };
  }
  if (region === "CA") {
    return {
      ...base,
      taxIdType: "BN_GST",
      vatLabel: "BN/GST",
      vatPlaceholder: "加拿大商业编号 BN 或 GST/HST",
      stateLabel: "省",
      statePlaceholder: "如 ON / BC",
      zipLabel: "邮编",
      zipPlaceholder: "Postal Code"
    };
  }
  if (region === "JP") {
    return {
      ...base,
      taxIdType: "JCT",
      vatLabel: "法人番号/JCT",
      vatPlaceholder: "法人番号或消费税登记号 JCT",
      stateLabel: "都道府县",
      statePlaceholder: "如 東京都",
      zipLabel: "邮编",
      zipPlaceholder: "郵便番号"
    };
  }
  if (region === "MX") {
    return {
      ...base,
      taxIdType: "RFC",
      vatLabel: "RFC",
      vatPlaceholder: "墨西哥税号 RFC",
      stateLabel: "州",
      statePlaceholder: "Estado",
      zipLabel: "邮编",
      zipPlaceholder: "C.P."
    };
  }
  if (region === "BR") {
    return {
      ...base,
      taxIdType: "CNPJ",
      vatLabel: "CNPJ",
      vatPlaceholder: "巴西公司税号 CNPJ（个人可用 CPF）",
      showIe: true,
      stateLabel: "州",
      statePlaceholder: "如 SP / RJ",
      zipLabel: "CEP",
      zipPlaceholder: "巴西邮编 CEP"
    };
  }
  if (region === "UK") {
    return {
      ...base,
      taxIdType: "VAT",
      vatLabel: "VAT",
      vatPlaceholder: "英国 VAT 号",
      showEori: true,
      eoriPlaceholder: "GB 开头 EORI",
      stateLabel: "地区",
      statePlaceholder: "County / Region",
      zipLabel: "Postcode",
      zipPlaceholder: "邮编"
    };
  }
  if (region === "EU") {
    return {
      ...base,
      taxIdType: "VAT",
      vatLabel: "VAT",
      vatPlaceholder: "欧盟 VAT 号",
      showEori: true,
      eoriPlaceholder: "欧盟 EORI",
      showIoss: true,
      stateLabel: "省/地区",
      statePlaceholder: "省或地区",
      zipLabel: "邮编",
      zipPlaceholder: "Postal Code"
    };
  }
  return base;
}

function onlyDigits(s) {
  return String(s || "").replace(/\D/g, "");
}

function isValidCnpj(raw) {
  const cnpj = onlyDigits(raw);
  if (cnpj.length !== 14 || /^(\d)\1+$/.test(cnpj)) return false;
  const calc = (base, factors) => {
    let sum = 0;
    for (let i = 0; i < factors.length; i++) sum += parseInt(base[i], 10) * factors[i];
    const mod = sum % 11;
    return mod < 2 ? 0 : 11 - mod;
  };
  const d1 = calc(cnpj, [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);
  const d2 = calc(cnpj, [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);
  return d1 === parseInt(cnpj[12], 10) && d2 === parseInt(cnpj[13], 10);
}

/** @returns {string|null} */
export function validateImporterTaxFields(form) {
  const meta = resolveImporterTaxMeta(form.country);
  const vat = String(form.vat || "").trim();
  const eori = String(form.eori || "").trim();
  const ioss = String(form.ioss || "").trim();
  const bondNo = String(form.bondNo || "").trim();
  const bondType = String(form.bondType || "").trim();
  const ie = String(form.ie || "").trim();

  if (!vat) return `请输入${meta.vatLabel}`;

  if (meta.region === "US") {
    if (onlyDigits(vat).length !== 9) return "EIN 须为 9 位数字（可带连字符，如 12-3456789）";
    if (bondType && bondType !== "none" && !bondNo) return "已选保函类型时请填写保函号";
  }
  if (meta.region === "MX") {
    if (!/^[A-Z&Ñ]{3,4}\d{6}[A-Z0-9]{3}$/i.test(vat.replace(/\s/g, ""))) return "RFC 格式不正确";
  }
  if (meta.region === "BR") {
    const digits = onlyDigits(vat);
    if (digits.length === 14) {
      if (!isValidCnpj(vat)) return "CNPJ 校验位不正确";
    } else if (digits.length !== 11) {
      return "CNPJ 须 14 位数字，或 CPF 11 位";
    }
  }
  if (meta.region === "JP") {
    const digits = onlyDigits(vat);
    if (digits.length !== 13 && digits.length !== 14) {
      return "法人番号一般为 13 位，JCT 登记号常见 14 位数字";
    }
  }
  if (meta.region === "CA" && onlyDigits(vat).length < 9) return "BN/GST 至少含 9 位数字";
  if (meta.showEori && eori) {
    if (meta.region === "UK" && !/^GB/i.test(eori)) return "英国 EORI 一般以 GB 开头";
    if (meta.region === "EU" && eori.length < 8) return "EORI 长度过短";
  }
  if (meta.showIoss && ioss && !/^IM/i.test(ioss) && ioss.length < 6) {
    return "IOSS 号格式不正确（常见 IM 开头）";
  }
  if (meta.showIe && ie && ie.length < 3) return "IE 州登记号过短";
  return null;
}

export function formatImporterOptionLabel(item) {
  if (!item) return "";
  const meta = resolveImporterTaxMeta(item.country);
  const parts = [item.name || item.ename];
  if (item.vat) parts.push(`${meta.vatLabel}:${item.vat}`);
  if (item.city || item.state || item.zip) {
    parts.push([item.city, item.state, item.zip].filter(Boolean).join(" "));
  } else if (item.address) {
    parts.push(item.address);
  }
  return parts.filter(Boolean).join(" | ");
}
